import { useCallback, useEffect, useMemo, useState } from 'react';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';
import '../styles/admin-usage.css';

function adminFetch(url: string, pseudo: string): Promise<Response> {
  return fetch(url, { headers: { 'X-Admin-Pseudo': pseudo } });
}

type EspaceFilter = 'all' | 'detente' | 'compet';

interface OverviewByDay {
  day: string;
  sessions: string;
  unique_players: string;
  unique_detente: string;
  unique_compet: string;
  avg_duration_sec: string | null;
}

interface OverviewResponse {
  by_day: OverviewByDay[];
  kpis: {
    dau: string;
    wau: string;
    mau: string;
    sessions_today: string;
    avg_duration_sec: string | null;
  };
  players: { new_players: string; total_players: string };
}

interface HeatmapCell {
  dow: number;
  hour: number;
  sessions: string;
}

interface RetentionResponse {
  eligible_d1: string; retained_d1: string;
  eligible_d7: string; retained_d7: string;
  eligible_d30: string; retained_d30: string;
}

interface TableStatRow {
  table_type: string;
  espace: string | null;
  session_count: string;
  unique_players: string;
  avg_duration_sec: string | null;
  total_duration_sec: string | null;
}

interface FeaturesResponse {
  direct_messages: { total: string; unique_senders: string };
  global_chat: Array<{ room_id: string; total: string; unique_senders: string }>;
  library: {
    games_saved: string; games_shared: string; shared_views: string;
    comments: string; unique_authors: string;
  };
  invites: { invite_messages: string; invites_sent: string; invites_accepted: string };
  training: { sessions_started: string; unique_players: string };
  page_views: Array<{ page: string; views: string; unique_players: string }>;
  availability: Array<{ availability: string; count: string }>;
}

const DOW_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const TABLE_TYPE_LABELS: Record<string, string> = {
  live: 'Live',
  diff_indiv: 'Différé individuel',
  diff_tq: 'Différé équipes',
  libre: 'Tables libres',
  defis: 'Défis',
  neo_libre: 'Neo — libre',
  neo_compet: 'Neo — compétition',
};
const PAGE_LABELS: Record<string, string> = {
  home: 'Accueil',
  chat: 'Chat',
  tables: 'Tables',
  game: 'Partie',
  rankings: 'Classements',
  competitions: 'Compétitions',
  training: 'Entraînement',
  library: 'Bibliothèque',
  forums: 'Forums',
  settings: 'Réglages',
  subscription: 'Abonnement',
  mySubscription: 'Mon abonnement',
  other: 'Autre',
};
const AVAILABILITY_LABELS: Record<string, string> = {
  bougon: 'Bougon',
  timide: 'Timide',
  detendu: 'Détendu',
  enthousiaste: 'Enthousiaste',
  on_fire: 'On fire !',
};

function n(value: string | number | null | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function pct(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0;
  return Math.round((numerator / denominator) * 100);
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours >= 1) return `${hours} h ${minutes.toString().padStart(2, '0')} min`;
  return `${minutes} min`;
}

function formatHours(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  return `${(seconds / 3600).toFixed(1)} h`;
}

function BarRow({ label, value, max, sublabel }: { label: string; value: number; max: number; sublabel?: string }) {
  const width = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="usage-bar-row">
      <div className="usage-bar-row-label">{label}</div>
      <div className="usage-bar-row-track">
        <div className="usage-bar-row-fill" style={{ width: `${width}%` }} />
      </div>
      <div className="usage-bar-row-value">{sublabel ?? value.toLocaleString('fr-FR')}</div>
    </div>
  );
}

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="usage-kpi-card">
      <p className="usage-kpi-label">{label}</p>
      <p className="usage-kpi-value">{value}</p>
      {hint && <p className="usage-kpi-hint">{hint}</p>}
    </div>
  );
}

interface AdminUsagePanelProps {
  session: Session;
}

export default function AdminUsagePanel({ session }: AdminUsagePanelProps) {
  const [days, setDays] = useState(30);
  const [espace, setEspace] = useState<EspaceFilter>('all');
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapCell[] | null>(null);
  const [retention, setRetention] = useState<RetentionResponse | null>(null);
  const [tables, setTables] = useState<TableStatRow[] | null>(null);
  const [features, setFeatures] = useState<FeaturesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const pseudo = session.pseudo;
    const qs = `days=${days}&espace=${espace}`;
    try {
      const [overviewRes, heatmapRes, retentionRes, tablesRes, featuresRes] = await Promise.all([
        adminFetch(`${BACKEND_URL}/api/admin/usage/overview?${qs}&pseudo=${encodeURIComponent(pseudo)}`, pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/usage/heatmap?${qs}&pseudo=${encodeURIComponent(pseudo)}`, pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/usage/retention?espace=${espace}&pseudo=${encodeURIComponent(pseudo)}`, pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/usage/tables?${qs}&pseudo=${encodeURIComponent(pseudo)}`, pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/usage/features?days=${days}&pseudo=${encodeURIComponent(pseudo)}`, pseudo),
      ]);

      if (!overviewRes.ok || !heatmapRes.ok || !retentionRes.ok || !tablesRes.ok || !featuresRes.ok) {
        throw new Error('Erreur lors du chargement des statistiques');
      }

      setOverview(await overviewRes.json());
      setHeatmap((await heatmapRes.json()).cells);
      setRetention(await retentionRes.json());
      setTables((await tablesRes.json()).tables);
      setFeatures(await featuresRes.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [session.pseudo, days, espace]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const heatmapMatrix = useMemo(() => {
    const matrix: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    let max = 0;
    for (const cell of heatmap ?? []) {
      const dowIndex = ((cell.dow % 7) + 7 - 1) % 7; // ISODOW 1=Lundi..7=Dimanche -> index 0..6
      const value = n(cell.sessions);
      matrix[dowIndex][cell.hour] = value;
      if (value > max) max = value;
    }
    return { matrix, max };
  }, [heatmap]);

  const maxSessionsPerDay = useMemo(
    () => Math.max(1, ...(overview?.by_day ?? []).map(d => n(d.sessions))),
    [overview],
  );

  const maxTableDuration = useMemo(
    () => Math.max(1, ...(tables ?? []).map(t => n(t.total_duration_sec))),
    [tables],
  );

  const maxPageViews = useMemo(
    () => Math.max(1, ...(features?.page_views ?? []).map(p => n(p.views))),
    [features],
  );

  const maxAvailability = useMemo(
    () => Math.max(1, ...(features?.availability ?? []).map(a => n(a.count))),
    [features],
  );

  if (loading && !overview) {
    return <div className="admin-empty">Chargement des statistiques d'usage…</div>;
  }

  if (error) {
    return <p className="usage-error">{error}</p>;
  }

  const dau = n(overview?.kpis.dau);
  const mau = n(overview?.kpis.mau);
  const stickiness = pct(dau, mau);

  return (
    <div className="usage-admin-page">
      <header className="usage-admin-hero">
        <div>
          <p className="usage-admin-kicker">Exploitation Neo</p>
          <h2 className="usage-admin-title">Statistiques d'usage</h2>
        </div>
        <div className="usage-admin-filters">
          <select value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>7 derniers jours</option>
            <option value={30}>30 derniers jours</option>
            <option value={90}>90 derniers jours</option>
          </select>
          <select value={espace} onChange={e => setEspace(e.target.value as EspaceFilter)}>
            <option value="all">Détente + Compétition</option>
            <option value="detente">Détente uniquement</option>
            <option value="compet">Compétition uniquement</option>
          </select>
        </div>
      </header>

      <section className="usage-kpi-grid">
        <KpiCard label="Joueurs actifs aujourd'hui (DAU)" value={dau.toLocaleString('fr-FR')} />
        <KpiCard label="Actifs sur 7 jours (WAU)" value={n(overview?.kpis.wau).toLocaleString('fr-FR')} />
        <KpiCard label="Actifs sur 30 jours (MAU)" value={mau.toLocaleString('fr-FR')} />
        <KpiCard label="Stickiness (DAU/MAU)" value={`${stickiness}%`} hint="Fidélité au quotidien" />
        <KpiCard
          label="Durée moyenne de session"
          value={formatDuration(n(overview?.kpis.avg_duration_sec))}
        />
        <KpiCard
          label="Nouveaux joueurs (période)"
          value={n(overview?.players.new_players).toLocaleString('fr-FR')}
          hint={`sur ${n(overview?.players.total_players).toLocaleString('fr-FR')} au total`}
        />
      </section>

      <section className="usage-card">
        <h3 className="usage-card-title">Fréquentation — évolution</h3>
        <div className="usage-daybars">
          {(overview?.by_day ?? []).map(d => (
            <div key={d.day} className="usage-daybar-col" title={`${d.day} : ${d.sessions} session(s), ${d.unique_players} joueur(s)`}>
              <div className="usage-daybar-track">
                <div
                  className="usage-daybar-fill"
                  style={{ height: `${Math.max(2, Math.round((n(d.sessions) / maxSessionsPerDay) * 100))}%` }}
                />
              </div>
              <span className="usage-daybar-label">
                {new Date(d.day).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
              </span>
            </div>
          ))}
          {(overview?.by_day ?? []).length === 0 && <p className="usage-empty-note">Pas encore de données de session.</p>}
        </div>
      </section>

      <section className="usage-card">
        <h3 className="usage-card-title">Fréquentation par jour de la semaine et heure</h3>
        <div className="usage-heatmap">
          <div className="usage-heatmap-hours">
            {Array.from({ length: 24 }, (_, h) => (
              <span key={h}>{h % 3 === 0 ? h : ''}</span>
            ))}
          </div>
          {heatmapMatrix.matrix.map((row, dowIdx) => (
            <div key={dowIdx} className="usage-heatmap-row">
              <span className="usage-heatmap-row-label">{DOW_LABELS[dowIdx]}</span>
              <div className="usage-heatmap-cells">
                {row.map((value, hour) => {
                  const intensity = heatmapMatrix.max > 0 ? value / heatmapMatrix.max : 0;
                  return (
                    <div
                      key={hour}
                      className="usage-heatmap-cell"
                      style={{ opacity: value > 0 ? 0.15 + intensity * 0.85 : 0.05 }}
                      title={`${DOW_LABELS[dowIdx]} ${hour}h : ${value} session(s)`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="usage-grid-2">
        <div className="usage-card">
          <h3 className="usage-card-title">Rétention — les joueurs reviennent-ils ?</h3>
          <p className="usage-card-subtitle">
            Part des joueurs actifs à nouveau après leur première session (fenêtres glissantes).
          </p>
          <div className="usage-retention-grid">
            <div className="usage-retention-cell">
              <p className="usage-retention-value">{pct(n(retention?.retained_d1), n(retention?.eligible_d1))}%</p>
              <p className="usage-retention-label">Retour à J1</p>
              <p className="usage-retention-sample">{retention?.eligible_d1 ?? 0} joueur(s) éligible(s)</p>
            </div>
            <div className="usage-retention-cell">
              <p className="usage-retention-value">{pct(n(retention?.retained_d7), n(retention?.eligible_d7))}%</p>
              <p className="usage-retention-label">Retour entre J1 et J7</p>
              <p className="usage-retention-sample">{retention?.eligible_d7 ?? 0} joueur(s) éligible(s)</p>
            </div>
            <div className="usage-retention-cell">
              <p className="usage-retention-value">{pct(n(retention?.retained_d30), n(retention?.eligible_d30))}%</p>
              <p className="usage-retention-label">Retour entre J1 et J30</p>
              <p className="usage-retention-sample">{retention?.eligible_d30 ?? 0} joueur(s) éligible(s)</p>
            </div>
          </div>
        </div>

        <div className="usage-card">
          <h3 className="usage-card-title">Disponibilité pour jouer</h3>
          <p className="usage-card-subtitle">Répartition actuelle des profils de disponibilité.</p>
          {(features?.availability ?? []).map(a => (
            <BarRow
              key={a.availability}
              label={AVAILABILITY_LABELS[a.availability] ?? a.availability}
              value={n(a.count)}
              max={maxAvailability}
            />
          ))}
          {(features?.availability ?? []).length === 0 && <p className="usage-empty-note">Aucune donnée.</p>}
        </div>
      </section>

      <section className="usage-card">
        <h3 className="usage-card-title">Temps passé sur les tables de jeu</h3>
        <p className="usage-card-subtitle">Par type de table, sur la période sélectionnée.</p>
        {(tables ?? []).map(t => (
          <BarRow
            key={`${t.table_type}-${t.espace}`}
            label={`${TABLE_TYPE_LABELS[t.table_type] ?? t.table_type}${t.espace ? ` (${t.espace === 'compet' ? 'compét.' : 'détente'})` : ''}`}
            value={n(t.total_duration_sec)}
            max={maxTableDuration}
            sublabel={`${formatHours(n(t.total_duration_sec))} · ${t.session_count} session(s) · ${t.unique_players} joueur(s)`}
          />
        ))}
        {(tables ?? []).length === 0 && <p className="usage-empty-note">Pas encore de données de table.</p>}
      </section>

      <section className="usage-grid-2">
        <div className="usage-card">
          <h3 className="usage-card-title">Chat &amp; messagerie</h3>
          {(features?.global_chat ?? []).map(g => (
            <div key={g.room_id} className="usage-stat-line">
              <span>Chat global — {g.room_id === 'global:compet' ? 'compétition' : 'détente'}</span>
              <strong>{n(g.total).toLocaleString('fr-FR')} message(s) · {g.unique_senders} joueur(s)</strong>
            </div>
          ))}
          <div className="usage-stat-line">
            <span>Messagerie directe</span>
            <strong>
              {n(features?.direct_messages.total).toLocaleString('fr-FR')} message(s) ·{' '}
              {n(features?.direct_messages.unique_senders)} joueur(s)
            </strong>
          </div>
          <div className="usage-stat-line">
            <span>Invitations envoyées</span>
            <strong>{n(features?.invites.invites_sent).toLocaleString('fr-FR')}</strong>
          </div>
          <div className="usage-stat-line">
            <span>Invitations acceptées</span>
            <strong>
              {n(features?.invites.invites_accepted).toLocaleString('fr-FR')}
              {' '}({pct(n(features?.invites.invites_accepted), n(features?.invites.invites_sent))}%)
            </strong>
          </div>
        </div>

        <div className="usage-card">
          <h3 className="usage-card-title">Bibliothèque &amp; entraînement</h3>
          <div className="usage-stat-line">
            <span>Parties enregistrées</span>
            <strong>{n(features?.library.games_saved).toLocaleString('fr-FR')} par {features?.library.unique_authors ?? 0} joueur(s)</strong>
          </div>
          <div className="usage-stat-line">
            <span>Parties partagées</span>
            <strong>{n(features?.library.games_shared).toLocaleString('fr-FR')}</strong>
          </div>
          <div className="usage-stat-line">
            <span>Consultations de partages</span>
            <strong>{n(features?.library.shared_views).toLocaleString('fr-FR')}</strong>
          </div>
          <div className="usage-stat-line">
            <span>Commentaires</span>
            <strong>{n(features?.library.comments).toLocaleString('fr-FR')}</strong>
          </div>
          <div className="usage-stat-line">
            <span>Sessions d'entraînement</span>
            <strong>{n(features?.training.sessions_started).toLocaleString('fr-FR')} par {features?.training.unique_players ?? 0} joueur(s)</strong>
          </div>
        </div>
      </section>

      <section className="usage-card">
        <h3 className="usage-card-title">Pages consultées</h3>
        {(features?.page_views ?? []).map(p => (
          <BarRow
            key={p.page}
            label={PAGE_LABELS[p.page] ?? p.page}
            value={n(p.views)}
            max={maxPageViews}
            sublabel={`${n(p.views).toLocaleString('fr-FR')} vue(s) · ${p.unique_players} joueur(s)`}
          />
        ))}
        {(features?.page_views ?? []).length === 0 && (
          <p className="usage-empty-note">Pas encore de données de navigation (le tracking vient d'être activé).</p>
        )}
      </section>
    </div>
  );
}
